import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicOpenScheduleDto, PublicSchoolDto } from '@motoschool/shared';
import { DEFAULT_LESSON_MINUTES, formatMoney } from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  PageLoader,
  Sheet,
  SuccessNotice,
  Textarea,
  cn,
} from '../components/ui';
import { PublicTimePicker, type PickedLesson } from '../components/PublicTimePicker';
import { lessonDurationLabel } from '../lib/useTeachers';
import { formatTime, humanDayLabel } from '../lib/format';

/**
 * Запись на занятие для ученика, который уже вошёл.
 *
 * Гостю то же самое показывает публичная страница, но здесь заявка уходит от
 * имени аккаунта: имя и телефон спрашивать не нужно, а инструктор сразу видит,
 * кто просится. Без этой страницы вошедший ученик записаться не мог вовсе —
 * в его расписании только собственные занятия.
 */
export function BookLessonPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [teacherId, setTeacherId] = useState<string | null>(null);
  const [picked, setPicked] = useState<PickedLesson | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSent, setIsSent] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['public', 'schedule'],
    queryFn: () =>
      api.get<PublicOpenScheduleDto>('/public/schedule', undefined, { anonymous: true }),
  });

  const { data: school } = useQuery({
    queryKey: ['public', 'school'],
    queryFn: () => api.get<PublicSchoolDto>('/public/school', undefined, { anonymous: true }),
  });

  const teachers = data?.teachers ?? [];
  const activeTeacher = teacherId ? teachers.find((item) => item.id === teacherId) : null;

  const visible = useMemo(() => {
    const source = activeTeacher ? [activeTeacher] : teachers;
    const withName = source.length > 1;

    return source.map((teacher) => ({
      teacherId: teacher.id,
      teacherName: withName ? teacher.name : undefined,
      hourlyRateMinor: teacher.hourlyRateMinor,
      free: teacher.free,
      busy: teacher.busy,
    }));
  }, [teachers, activeTeacher]);

  const send = useMutation({
    mutationFn: () =>
      api.post('/requests', {
        teacherId: picked?.teacherId,
        startsAt: picked?.startsAt,
        durationMinutes: picked?.durationMinutes,
        comment: comment.trim() || null,
      }),
    onSuccess: async () => {
      setPicked(null);
      setComment('');
      setError(null);
      setIsSent(true);
      await queryClient.invalidateQueries({ queryKey: ['public', 'schedule'] });
      await queryClient.invalidateQueries({ queryKey: ['requests'] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError ? caught.message : 'Не удалось отправить заявку',
      );
    },
  });

  const timeZone = data?.timeZone ?? 'Asia/Almaty';
  const currency = data?.currency ?? 'KZT';

  if (isLoading) return <PageLoader />;

  const endsAt =
    picked === null
      ? null
      : new Date(new Date(picked.startsAt).getTime() + picked.durationMinutes * 60_000);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Записаться на занятие</h1>
        <p className="text-sm text-muted">
          Выберите день, начало и сколько часов ездить — инструктор подтвердит запись
        </p>
      </div>

      {isSent && (
        <SuccessNotice>
          Заявка отправлена. Инструктор подтвердит запись — следите за разделом{' '}
          <Link to="/my-requests" className="font-medium underline">
            «Заявки»
          </Link>
          .
        </SuccessNotice>
      )}

      {teachers.length === 0 ? (
        <EmptyState
          title="Свободного времени нет"
          description="Инструкторы ещё не задали часы работы. Загляните позже."
        />
      ) : (
        <>
          {/* Фильтр по инструктору: при одном инструкторе он не нужен */}
          {teachers.length > 1 && (
            <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
              <button
                type="button"
                onClick={() => setTeacherId(null)}
                className={cn(
                  'min-h-10 shrink-0 rounded-full border px-4 text-sm font-medium',
                  teacherId === null
                    ? 'border-brand-600 bg-brand-600 text-white'
                    : 'border-line bg-surface text-muted',
                )}
              >
                Все инструкторы
              </button>
              {teachers.map((teacher) => (
                <button
                  key={teacher.id}
                  type="button"
                  onClick={() => setTeacherId(teacher.id)}
                  className={cn(
                    'flex min-h-10 shrink-0 items-center rounded-full border px-4 text-sm font-medium',
                    teacherId === teacher.id
                      ? 'border-brand-600 bg-brand-600 text-white'
                      : 'border-line bg-surface text-muted',
                  )}
                >
                  {teacher.name}
                </button>
              ))}
            </div>
          )}

          <PublicTimePicker
            teachers={visible}
            timeZone={timeZone}
            currency={currency}
            defaultMinutes={school?.defaultLessonMinutes ?? DEFAULT_LESSON_MINUTES}
            onPick={(lesson) => {
              setIsSent(false);
              setError(null);
              setPicked(lesson);
            }}
          />
        </>
      )}

      <Sheet
        isOpen={picked !== null}
        title="Заявка на занятие"
        onClose={() => setPicked(null)}
        footer={
          <div className="flex gap-2 pb-1">
            <Button variant="secondary" fullWidth onClick={() => setPicked(null)}>
              Отмена
            </Button>
            <Button fullWidth isLoading={send.isPending} onClick={() => send.mutate()}>
              Отправить
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {error && <ErrorNotice message={error} />}

          {picked && endsAt && (
            <div className="rounded-xl bg-canvas px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm">{humanDayLabel(picked.startsAt, timeZone)}</span>
                <Badge tone="brand">
                  {formatTime(picked.startsAt, timeZone)} —{' '}
                  {formatTime(endsAt.toISOString(), timeZone)}
                </Badge>
              </div>
              <p className="mt-1 text-xs text-muted">
                {picked.teacherName ? `${picked.teacherName} · ` : ''}
                {lessonDurationLabel(picked.durationMinutes)}
                {picked.priceMinor === null ? '' : ` · ${formatMoney(picked.priceMinor, currency)}`}
              </p>
            </div>
          )}

          <Field label="Комментарий" hint="Что хотите отработать — необязательно">
            <Textarea
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Хочу отработать змейку"
            />
          </Field>
        </div>
      </Sheet>

      <Card className="bg-brand-50/60">
        <p className="text-sm">
          Занятие спишется с баланса, когда инструктор его проведёт.{' '}
          <Link to="/balance" className="font-medium text-brand-600">
            Посмотреть баланс
          </Link>
        </p>
      </Card>
    </div>
  );
}
